import { ReactElement, useEffect, useState } from "react";
import { t } from "@lingui/core/macro";

import { MenuItem, OverlayMenu } from "_/components/overlay-menu";
import { SearchBox } from "_/components/search-box";
import { UserAvatar } from "_/components/avatar";
import { useLocation } from "_/components/router";
import { useAddToast } from "_/components/toasts";

import { User, useCheckUser, useLogoutUser } from "_/data/users";
import {
  useCheckCurrentOrg,
  useCheckAdminAccess,
  useMemberOrgs,
} from "_/data/orgs";
import { isInvitePending, useUserInvites } from "_/data/invitations";

import { useSetLocationAndOrg } from "_/hooks/useSetLocationAndOrg";

import { Uuid } from "_/types";
import { Select } from "_/components/select";
import { adminUrls, routeUrls, settingsUrls } from "_/routes";

import * as S from "./styled";

const OrgPicker = (): ReactElement => {
  const { data: currentOrg } = useCheckCurrentOrg();
  const { data: orgs } = useMemberOrgs();
  const setLocationAndOrg = useSetLocationAndOrg();

  if (!orgs || orgs.length <= 1) {
    return <></>;
  }

  const orgOptions = orgs.map((org) => {
    return {
      label: org.name,
      value: org.id,
    };
  });

  return (
    <S.SelectWrapper>
      <Select
        options={orgOptions}
        value={currentOrg?.id}
        isSearchable={false}
        onChange={(orgId: Uuid) => {
          setLocationAndOrg({ orgId, url: routeUrls.index });
        }}
      />
    </S.SelectWrapper>
  );
};

/**
 * Temporary hook to show a toast when user has pending invites.
 *
 * This component should not be extended or built upon and is only a temporary
 * UX until we can implement a more complete solution for generalized
 * notifications. If you are about to extend this to have additional
 * functionality, stop and please consider reprioritizing so that we can build
 * a more robust and generalized solution.
 */
const useShowInvitesToast = () => {
  const { data: invites = [] } = useUserInvites();
  const addToast = useAddToast();
  const [shown, setShown] = useState(false);
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (shown) {
      return;
    }

    const filteredInvites = invites.filter(isInvitePending);

    // Only show the toast if there are pending invites.
    if (filteredInvites.length <= 0) {
      return;
    }

    // If the user is already on the invitations page, don't show the toast.
    if (location === settingsUrls.invitations) {
      return;
    }

    setShown(true);
    addToast({
      title: t`toasts.pending-invites.title`,
      content: t`toasts.pending-invites.content ${filteredInvites.length}`,
      kind: "info",
      timeout: 0,
      actions: [
        {
          label: t`toasts.pending-invites.review-invites-action`,
          action: () => setLocation(routeUrls.settings.invitations),
        },
      ],
    });
  }, [addToast, shown, setShown, invites, location, setLocation]);
};

const UserMenu = ({ user }: { user: User }): ReactElement => {
  const [_, setLocation] = useLocation();
  const logoutUser = useLogoutUser();
  const checkAdminAccess = useCheckAdminAccess();

  useShowInvitesToast();

  const menuItems: (MenuItem | false | undefined)[] = [
    {
      icon: "Person",
      render: t`components.header.settings`,
      onClick: () => setLocation(settingsUrls.general),
    },
    checkAdminAccess === "yes" && {
      icon: "AdminPanelSettings",
      render: t`components.header.manage`,
      onClick: () => setLocation(adminUrls.index),
    },
    {
      icon: "Logout",
      render: t`components.header.logout`,
      onClick: () => {
        logoutUser.mutate(undefined, {
          onSuccess: () => {
            setLocation(routeUrls.public.login);
          },
        });
      },
    },
  ] as const;
  const filteredMenuItems = menuItems.filter(Boolean);

  const userAvatar = (
    <S.UserInfoContainer>
      <UserAvatar resource={user} />
      <S.UserName>{user.name}</S.UserName>
      <S.DownIcon variant="ArrowDropDown" />
    </S.UserInfoContainer>
  );

  return (
    <OverlayMenu
      placement="bottomEnd"
      target={userAvatar}
      items={filteredMenuItems}
    />
  );
};

export const Header = (): ReactElement => {
  const { data: user } = useCheckUser();
  const { data: currentOrg } = useCheckCurrentOrg();
  const [_, setLocation] = useLocation();

  function goHome() {
    if (user) {
      setLocation(routeUrls.index);
    } else {
      setLocation(routeUrls.public.login);
    }
  }

  // Both logos are included in the returned JSX, but styles will hide
  // one or the other depending on the screen size. This is the "cleanest"
  // way to do it while still being responsive without adding an additional
  // hook to check for screen size changes and selecting the desired component.
  return (
    <S.Wrapper>
      <S.TitleLogoFull onClick={goHome} $user={user} />
      <S.TitleLogoSquare onClick={goHome} $user={user} />
      {user && (
        <>
          <S.SearchWrapper>
            <SearchBox currentOrg={currentOrg} />
          </S.SearchWrapper>
          <S.Nav>
            <OrgPicker />
            <UserMenu user={user} />
          </S.Nav>
        </>
      )}
    </S.Wrapper>
  );
};
